import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  ArrowRight, 
  Lightbulb, 
  Zap, 
  Bot, 
  Brain,
  Users,
  Target,
  BarChart3,
  Shield,
  Workflow
} from 'lucide-react';
import IntakeModal from '../components/IntakeModal';

const Services = () => {
  const [isIntakeOpen, setIsIntakeOpen] = useState(false);
  const [intakeType, setIntakeType] = useState('client-zero');

  const openIntake = (type) => {
    setIntakeType(type);
    setIsIntakeOpen(true);
  };

  // Three ways Pecaro can help
  const serviceOfferings = [
    {
      icon: Lightbulb,
      title: "AI Strategy & Readiness",
      tagline: "Know where to start.",
      description: "For organizations that know AI matters but aren't sure where it belongs. We help assess workflows, systems, opportunities, implementation considerations and business priorities to develop a practical AI roadmap.",
      ideal: "\"How should we actually use AI?\"",
      includes: [
        "AI readiness assessment",
        "Workflow and process analysis",
        "Opportunity identification and prioritization",
        "Implementation considerations",
        "Practical AI roadmap development"
      ],
      cta: "Explore AI Strategy",
      link: "/book-consultation"
    },
    {
      icon: Zap,
      title: "AI Automation & Implementation",
      tagline: "Turn repetitive work into intelligent workflows.",
      description: "We help design and implement automation connecting your people, processes, approved AI technologies and existing business systems. From intake and customer communication to workflow coordination and internal processes.",
      ideal: "\"How can we stop doing this manually?\"",
      includes: [
        "Workflow automation design",
        "System integration",
        "Customer communication automation",
        "Process coordination",
        "Performance measurement"
      ],
      cta: "Explore Automation",
      link: "/book-consultation"
    },
    {
      icon: Bot,
      title: "AI Employees",
      tagline: "Give repetitive work to AI. Give valuable time back to people.",
      description: "Deploy AI employees such as AVA to support defined customer and operational workflows. Start with one job. Measure the result. Expand when the business case is proven.",
      ideal: "\"What if AI could actually do some of this work?\"",
      includes: [
        "AVA AI Employee configuration",
        "Workflow discovery and design",
        "Business rules and escalation setup",
        "Integration with existing systems",
        "30-day optimization period"
      ],
      cta: "Meet AVA",
      link: "/altam"
    }
  ];

  // What we consider
  const considerations = [
    { icon: Users, title: "People", description: "Who performs the work today? How will the change affect them?" },
    { icon: Workflow, title: "Process", description: "How does the work actually happen? Where are the bottlenecks?" },
    { icon: Brain, title: "Technology", description: "Which systems need to participate? What integrations are required?" },
    { icon: Target, title: "Customer Experience", description: "How will the change affect customers? Will it improve their experience?" },
    { icon: Shield, title: "Risk & Control", description: "Where should permissions, controls and human oversight exist?" },
    { icon: BarChart3, title: "Business Value", description: "What measurable outcome should the investment create?" }
  ];

  // Business case questions
  const businessCaseQuestions = [
    "How much employee time does this process consume?",
    "How frequently does it happen?",
    "What does that time cost the business?",
    "Where are delays affecting customers?",
    "Are opportunities being lost because people can't respond quickly enough?",
    "What measurable improvement would make the investment worthwhile?"
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F5F5F5] via-white to-[#F5F5F5]"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#227AFF]/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6">
              Three Ways to Put <span className="text-gradient">AI to Work</span> in Your Business
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              From strategy to implementation to AI employees — we help you identify where AI creates measurable value, then build the solution.
            </p>
            
            <p className="text-lg text-gray-500 mb-10">
              You don't need to transform your entire business. Start with one problem worth solving.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/book-consultation" className="btn btn-primary text-lg px-8">
                Book an AI Strategy Session
              </Link>
              <button onClick={() => openIntake('meet-ava')} className="btn btn-glass text-lg px-8 inline-flex items-center gap-2">
                Meet AVA <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Three Service Offerings */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="mb-4">How Pecaro Can Help</h2>
              <div className="accent-line"></div>
            </div>

            <div className="space-y-8">
              {serviceOfferings.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                    <div className="grid md:grid-cols-3 gap-8">
                      <div className="md:col-span-2">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="p-3 rounded-xl bg-[#227AFF]/10">
                            <IconComponent className="h-8 w-8 text-[#227AFF]" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold text-gray-800">{service.title}</h3>
                            <p className="text-[#227AFF] font-medium">{service.tagline}</p>
                          </div>
                        </div>
                        
                        <p className="text-gray-600 mb-6">{service.description}</p>
                        
                        <div className="bg-gray-50 rounded-lg p-4 mb-6">
                          <p className="text-sm text-gray-500">Ideal for businesses asking:</p>
                          <p className="text-gray-700 font-medium italic">{service.ideal}</p>
                        </div>
                        
                        <Link to={service.link} className="btn btn-primary inline-flex items-center gap-2">
                          {service.cta} <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                      
                      <div className="bg-gray-50 rounded-xl p-6">
                        <p className="font-semibold text-gray-800 mb-4">Includes:</p>
                        <ul className="space-y-3">
                          {service.includes.map((item, idx) => (
                            <li key={idx} className="flex items-start">
                              <CheckCircle className="h-5 w-5 text-[#227AFF] mr-2 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 text-sm">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* AI Has to Earn Its Place */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="mb-4">AI Has to Earn Its Place in Your Business</h2>
              <div className="accent-line"></div>
              <p className="text-lg text-gray-600 mt-6">
                We don't recommend automation simply because something <em>can</em> be automated.
              </p>
              <p className="text-xl font-semibold text-[#0A1538] mt-2">The business case has to make sense.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
              <p className="text-gray-700 mb-6 font-medium">Before implementing AI, we look at questions such as:</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {businessCaseQuestions.map((question, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#227AFF] flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{question}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#0A1538] rounded-xl p-6 text-center">
              <p className="text-gray-300 mb-2">If the business case isn't compelling, it probably shouldn't be the first thing you automate.</p>
              <p className="text-gray-400 mb-2">AI isn't the strategy.</p>
              <p className="text-xl font-semibold text-white">Better business performance is.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Consider */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="mb-4">We Look Beyond the AI Model</h2>
              <div className="accent-line"></div>
              <p className="text-lg text-gray-600 mt-6">
                Technology alone doesn't transform a business. Implementation does.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {considerations.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-[#227AFF]/10">
                        <IconComponent className="h-5 w-5 text-[#227AFF]" />
                      </div>
                      <h4 className="font-semibold text-gray-800">{item.title}</h4>
                    </div>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </div>
                );
              })}
            </div>

            <div className="bg-gradient-to-r from-[#227AFF]/5 to-[#007BFF]/5 rounded-xl p-6 text-center">
              <p className="text-gray-700">Then we design the technology around the business.</p>
              <p className="text-xl font-semibold text-[#0A1538] mt-2">Not the other way around.</p>
            </div>
          </div>
        </div>
      </section>

      {/* AVA Feature */}
      <section className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-gray-400 mb-2">Ready for AI Employees?</p>
            <h2 className="text-white mb-4">Meet AVA™</h2>
            <p className="text-3xl font-bold text-[#227AFF] mb-6">Your First AI Employee</p>
            
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              AVA is an AI employee powered by ALTAM™, designed to handle repetitive customer and operational work while keeping your people in control.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-10">
              {['Customer Service', 'Lead Qualification', 'Appointment Scheduling', 'Client Intake', 'Sales Follow-Up', 'Operations Support'].map((item, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                  <span className="text-white">{item}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => openIntake('meet-ava')} className="btn btn-accent inline-flex items-center gap-2">
                Meet AVA <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => openIntake('client-zero')} className="btn btn-glass inline-flex items-center gap-2" data-testid="apply-client-zero-btn">
                Apply for Client Zero <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-4">Ready to Find Your First AI Opportunity?</h2>
            <p className="text-xl text-gray-600 mb-8">
              You don't need a 12-month AI strategy to get started. You need one business problem worth solving.
            </p>
            
            <div className="bg-gradient-to-r from-[#227AFF]/5 to-[#007BFF]/5 rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 mb-2">Start With the Problem.</p>
              <p className="text-2xl font-bold text-[#0A1538]">We'll Help You Build the Solution.</p>
            </div>

            <Link to="/book-consultation" className="btn btn-primary text-lg px-8">
              Book Your AI Strategy Session
            </Link>
            
            <p className="text-sm text-gray-500 mt-6">
              We'll help you determine whether AI can create measurable value — and what to do next.
            </p>
          </div>
        </div>
      </section>

      {/* Intake Modal */}
      <IntakeModal 
        isOpen={isIntakeOpen} 
        onClose={() => setIsIntakeOpen(false)} 
        type={intakeType}
      />
    </div>
  );
};

export default Services;
